// The runtime configuration comes from the selected server. Guest, demo and public pages never mount this boundary.
import { createContext, useContext, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import type { HttpApi } from '../api/client.ts';
import { useStore } from '../store/hooks.tsx';

export interface AccountAuth {
  provider: 'email' | 'clerk' | 'loading' | 'unavailable';
  signIn?: ReactNode;
  error?: string | null;
  pending?: boolean;
  signOut(): Promise<void>;
  retry?(): void;
}

export const AuthContext = createContext<AccountAuth | null>(null);

export function useAccountAuth(): AccountAuth {
  const auth = useContext(AuthContext);
  const store = useStore();
  return auth ?? { provider: 'email', signOut: () => store.signOut() };
}

export interface ClerkAuthProps { api: HttpApi; publishableKey: string; children: ReactNode }

export function AuthBoundary({ api, children }: { api: HttpApi; children: ReactNode }) {
  const store = useStore();
  const [attempt, setAttempt] = useState(0);
  const [provider, setProvider] = useState<AccountAuth['provider']>('loading');
  const [clerk, setClerk] = useState<{ Component: ComponentType<ClerkAuthProps>; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    let current = true;
    setProvider('loading');
    void api.authConfig().then(async (config) => {
      if (config.provider === 'email') { if (current) setProvider('email'); return; }
      const { default: Component } = await import('./ClerkAuth.tsx');
      if (current) { setClerk({ Component, key: config.publishableKey }); setProvider('clerk'); }
    }).catch(() => { if (current) setProvider('unavailable'); });
    return () => { current = false; };
  }, [api, attempt]);
  const signOut = async () => {
    setPending(true);
    setError(null);
    try { await store.signOut(); }
    catch { setError(store.t('Die Abmeldung konnte nicht bestätigt werden. Prüfe die Verbindung und versuche es erneut.')); }
    finally { setPending(false); }
  };
  if (provider === 'clerk' && clerk) return <clerk.Component api={api} publishableKey={clerk.key}>{children}</clerk.Component>;
  return <AuthContext.Provider value={{ provider, error, pending, signOut, retry: () => setAttempt((n) => n + 1) }}>{children}</AuthContext.Provider>;
}
